interface UploadOptions {
  file: File;
  uploadUrl: string;
  key: string;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
  maxRetries?: number;
}

interface UploadResult {
  key: string;
  success: boolean;
  error?: string;
}

async function uploadWithRetry(options: UploadOptions): Promise<UploadResult> {
  const { file, uploadUrl, onProgress, signal, maxRetries = 3 } = options;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);

      const result = await new Promise<UploadResult>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ key: options.key, success: true });
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        if (signal) {
          signal.addEventListener('abort', () => xhr.abort());
        }

        xhr.send(file);
      });

      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  return {
    key: options.key,
    success: false,
    error: lastError?.message || 'Upload failed after retries',
  };
}

export async function uploadToR2(options: UploadOptions): Promise<UploadResult> {
  return uploadWithRetry(options);
}

export async function uploadMultiple(
  files: File[],
  getPresignedUrl: (file: File) => Promise<{ uploadUrl: string; key: string }>,
  onFileProgress?: (index: number, progress: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];

  for (let i = 0; i < files.length; i++) {
    if (signal?.aborted) {
      results.push({ key: files[i].name, success: false, error: 'Aborted' });
      continue;
    }

    const presigned = await getPresignedUrl(files[i]);
    const result = await uploadWithRetry({
      file: files[i],
      uploadUrl: presigned.uploadUrl,
      key: presigned.key,
      onProgress: (progress) => onFileProgress?.(i, progress),
      signal,
    });
    results.push(result);
  }

  return results;
}
